import React, { useMemo } from "react";
import {
  Sheet,
  SheetActions,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@src/lib/ui/Sheet";

import { gql } from "@apollo/client";
import Avatar from "@src/lib/ui/Avatar";
import Text from "@src/lib/ui/Text";
import PaginatedTable from "@src/lib/ui/PaginatedTable";
import InviteUsersDropdown from "@src/features/users/InviteUsersDropdown";
import {
  NotInvitedUsersSheetHrisEmployeeFragment,
  useNotInvitedUsersSheetHrisEmployeesLazyQuery,
  useNotInvitedUsersSheetHrisEmployeesQuery,
} from "@src/features/home/NotInvitedUsersSheet.generated";

type Props = {
  children: React.ReactNode;
};

type Column = "name";
const exportColumns = ["name"] as const;
type ExportColumn = (typeof exportColumns)[number];

const NotInvitedUsersSheet: React.FC<Props> = (props) => {
  const input = useMemo(
    () => ({
      filters: {
        inviteStatus: { value: false },
      },
    }),
    [],
  );
  return (
    <Sheet>
      <SheetTrigger asChild>{props.children}</SheetTrigger>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>Users not invited</SheetTitle>
          <SheetDescription>
            HRIS employees who have not been invited to Opus
          </SheetDescription>
          <SheetActions>
            <InviteUsersDropdown />
          </SheetActions>
        </SheetHeader>
        <PaginatedTable<
          NotInvitedUsersSheetHrisEmployeeFragment,
          Column,
          typeof useNotInvitedUsersSheetHrisEmployeesQuery,
          typeof useNotInvitedUsersSheetHrisEmployeesLazyQuery,
          ExportColumn
        >
          title={undefined}
          queryHook={useNotInvitedUsersSheetHrisEmployeesQuery}
          lazyQueryHook={useNotInvitedUsersSheetHrisEmployeesLazyQuery}
          getItems={(x) => x?.HrisEmployees.objects ?? []}
          getTotalItemCount={(x) => x?.HrisEmployees.totalCount ?? 0}
          itemId={(x) => x.id}
          input={input}
          allItemsCountInput={input}
          defaultSort={{ column: "name", descending: true }}
          allColumns={["name"]}
          columnTitles={{ name: "User" }}
          rowHeight={52}
          renderFns={{
            name: (x) => {
              return (
                <div className="flex flex-row items-center gap-2">
                  <Avatar
                    person={{ id: x.id, name: `${x.firstName} ${x.lastName}` }}
                  />
                  <div>
                    <Text variant="compact-title">
                      {x.firstName} {x.lastName}
                    </Text>
                  </div>
                </div>
              );
            },
          }}
        />
      </SheetContent>
    </Sheet>
  );
};

gql`
  query NotInvitedUsersSheetHrisEmployees(
    $input: HrisEmployeesInput!
    $pagination: PaginationInput
  ) {
    HrisEmployees(input: $input, pagination: $pagination) {
      totalCount
      objects {
        ...NotInvitedUsersSheetHrisEmployee
      }
    }
  }
  fragment NotInvitedUsersSheetHrisEmployee on HrisEmployee {
    id
    firstName
    lastName
    phoneNumber
  }
`;

export default NotInvitedUsersSheet;
